import { defineStore } from 'pinia'
import { useApi } from '../composables/useApi.js'
import { useNotification } from '@kyvg/vue3-notification'
import { i18n } from '../i18n/index.js'
import { useContactStore } from '@/stores/contacts'
import { useAuthStore } from '@/stores/auth'
import { ref } from 'vue'

export const useUserStore = defineStore('userStore', () => {
  const $t = i18n.global.t
  const Api = useApi()
  const contactsStore = useContactStore()
  const authStore = useAuthStore()
  const { notify } = useNotification()

  const changePasswordToken = ref('')
  const password = ref(null)
  const inviteCode = ref('')
  const loading = ref(false)
  const nextStep = ref(false)

  // Both helpers below are shared between two genuinely different subjects,
  // told apart by the same `type` discriminator the rest of the app uses:
  //
  //   type: 'user'    -> the *logged-in user's own* avatar. That lives on the
  //                      `users` table and is edited through /users/current
  //                      (see editUser/deleteUserAvatar in this same store) -
  //                      NOT through /contacts/:id, which is a contacts-only
  //                      endpoint family. Passing `authStore.user.id` to
  //                      /contacts/:id addressed an unrelated `contacts` row
  //                      (independent id sequences), so removing/replacing
  //                      your own Cabinet photo either 404'd or, if the
  //                      numbers happened to line up, touched someone else's
  //                      contact.
  //   type: 'contact' -> a `contacts` row the user owns (Cabinet/Profile/
  //                      RemovePhotoModal.vue's `contact` branch, reached
  //                      from Contacts/CardUser.vue) - /contacts/:id is
  //                      correct there, and the refetch afterwards needs the
  //                      matching `type` too.
  const isCurrentUserTarget = (params) => params.type === 'user'

  const refreshAvatarSubject = async (params) => {
    if (isCurrentUserTarget(params)) {
      await authStore.current()
      return
    }
    await contactsStore.getContactById({ id: params.id, type: 'contact' })
  }

  const updateImg = async (params) => {
    if (!params.id || !params.file) return false
    const formData = new FormData()

    formData.append(params.url, params.file)

    const { error } = await Api(
      isCurrentUserTarget(params) ? 'users/current' : `contacts/${params.id}`
    )
      .patch(formData)
      .json()
    if (error.value) {
      notify({
        title: $t('error_update_image'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }

    await refreshAvatarSubject(params)
  }

  const deleteImg = async (params) => {
    if (!params.id) return false
    const { error } = await Api(
      isCurrentUserTarget(params)
        ? 'users/current/delete-avatar'
        : `contacts/${params.id}/delete-avatar`
    )
      .delete()
      .json()

    if (error.value) {
      notify({
        title: $t('error_update_image'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
    await refreshAvatarSubject(params)
  }

  const deleteProfile = async () => {
    const { data, error } = await Api('users').delete().json()
    if (data.value) {
      notify({
        title: $t(`${data.value.message}_msg`),
        type: 'success',
        ignoreDuplicates: true
      })
      authStore.logout()
    } else {
      notify({
        title: $t(`${error.value.message}_msg`),
        type: 'error',
        ignoreDuplicates: true
      })
    }
  }

  const addEmail = async (params) => {
    const { data, error } = await Api('users/add-email').post(params).json()

    if (data.value) {
      return data.value
    } else {
      notify({
        title: $t('error'),
        text: $t(`${error.value}`),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const confirmEmail = async (params) => {
    const { data } = await Api('users/confirm-email').post(params).json()

    if (data.value) {
      return data.value
    } else {
      return 'error'
    }
  }

  const addPassword = async (params) => {
    const { error } = await Api('users/set-password')
      .post({
        password: params.password,
        confirmationPassword: params.repeatPassword
      })
      .json()

    if (error.value) {
      notify({
        title: $t('error'),
        text: $t(`${error.value}`),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const changeEmail = async (params) => {
    const { data, error } = await Api('users/change-email')
      .patch({
        email: params.email,
        password: params.password
      })
      .json()

    if (data.value) {
      nextStep.value = true
      return true
    } else {
      notify({
        title: $t('error'),
        text: $t(`${error.value}`),
        type: 'error',
        ignoreDuplicates: true
      })
      nextStep.value = false
      return 'error'
    }
  }

  const confirmChangeEmail = async (params) => {
    const { error } = await Api('users/confirm-change-email')
      .post(params)
      .json()

    if (error.value) {
      notify({
        title: $t('error'),
        text: $t(`${error.value}`),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const setPass = (params) => {
    password.value = params
  }

  const changePass = async (params) => {
    const { data, error } = await Api('users/change-password-request')
      .post(params)
      .json()

    if (error.value) {
      notify({
        title: $t('error_pass'),
        type: 'error',
        ignoreDuplicates: true
      })
    }

    if (data.value) {
      changePasswordToken.value = data.value.data.changePasswordToken
      return true
    }
  }

  const setNewPass = async (params) => {
    const { data, error } = await Api('users/set-new-password')
      .patch({ ...params, changePasswordToken: changePasswordToken.value })
      .json()

    if (error.value) {
      notify({
        title: $t('error_pass'),
        type: 'error',
        ignoreDuplicates: true
      })
    }

    if (data.value) {
      return true
    }
  }

  const changePhone = async (params) => {
    const { data, error } = await Api('users/change-phone').patch(params).json()

    if (error.value) {
      console.log('error')
      return 'error'
    }

    if (data.value) {
      return true
    }
  }

  const confirmPhone = async (params) => {
    const { data } = await Api('users/confirm-change-phone').post(params).json()

    if (data.value) {
      return data.value
    } else {
      return 'error'
    }
  }

  const resetPass = async () => {
    const { data } = await Api('users/reset-password').post().json()

    if (data.value) {
      return data.value
    } else {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
    }
  }

  const confirmReset = async (params) => {
    const { data } = await Api('users/confirm-reset-password')
      .post(params)
      .json()

    if (data.value) {
      return data.value
    } else {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const editUser = async (dataInfo) => {
    const formData = new FormData()
    if (typeof dataInfo?.avatar_url !== 'string' && dataInfo?.avatar_url) {
      formData.append('avatar', dataInfo?.avatar_url[0])
    }

    for (const key in dataInfo) {
      if (
        (key !== 'avatar_url' && key !== 'country' && dataInfo[key]) ||
        (typeof dataInfo[key] === 'string' && key !== 'avatar_url')
      ) {
        formData.append(key, dataInfo[key])
      }
    }
    const { data } = await Api('users/current').patch(formData).json()

    if (
      data?.value?.data &&
      dataInfo.current_language !== authStore.currentLocale
    ) {
      await authStore.current()

      notify({
        title: $t('update_profile'),
        type: 'success',
        ignoreDuplicates: true
      })
    }

    if (data.value) {
      return data.value
    } else {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }
  const getInviteCode = async () => {
    loading.value = true
    const { data } = await Api('users/current/invite-code').post().json()
    inviteCode.value = data?.value?.data?.invite_code
    loading.value = false
  }

  // const inviteLink = computed(() => {
  //   return `${location.value.origin}${
  //     router.resolve({ name: 'login' }).path
  //   }?invite=${inviteCode.value}`
  // })

  const deleteUserAvatar = async () => {
    const { error } = await Api('users/current/delete-avatar').delete().json()

    if (error.value) {
      notify({
        title: $t('error_update_image'),
        type: 'error',
        ignoreDuplicates: true
      })
    }
    // await contactsStore.getContactById({ id: params.id })
  }
  return {
    updateImg,
    deleteImg,
    addEmail,
    setPass,
    confirmEmail,
    confirmPhone,
    confirmChangeEmail,
    changeEmail,
    changePass,
    setNewPass,
    changePhone,
    addPassword,
    resetPass,
    confirmReset,
    deleteProfile,
    password,
    editUser,
    getInviteCode,
    inviteCode,
    deleteUserAvatar,
    // inviteLink,
    loading,
    nextStep
  }
})
